/** Rules for Markdown notes (spec04): title, preview and links come from the text itself. */

export const UNTITLED = "Untitled";
export const MAX_TITLE_LENGTH = 200;
export const MAX_NOTEBOOK_DEPTH = 3;
export const TRASH_RETENTION_DAYS = 30;

const lines = (content: string) => content.split(/\r?\n/);

/** What an empty title shows as (FR9). */
export const displayTitle = (title: string) => title.trim() || UNTITLED;

/**
 * Markdown marks removed, for one-line previews. Not a renderer: output is plain text.
 * Only the start of the line is used and no pattern scans past a bracket or parenthesis, so
 * a crafted line cannot make it slow (CodeQL ReDoS).
 */
function plain(line: string): string {
  return line
    .slice(0, 1000)
    .replace(/^\s*(?:#{1,6}\s+|>\s?|[-*+]\s+(?:\[[ xX]\]\s+)?|\d+[.)]\s+)/, "")
    .replace(
      /!?\[\[([^[\]|]+)(?:\|([^[\]]+))?\]\]/g,
      (_m, target: string, alias?: string) => alias ?? target,
    )
    .replace(/!?\[([^[\]]*)\]\([^()]*\)/g, "$1")
    .replace(/(\*\*|__|~~|`)/g, "")
    .trim();
}

/** The first line of text, for the note list (max 200 chars). */
export function notePreview(content: string): string {
  for (const line of lines(content)) {
    if (/^\s*(```|~~~|\$\$|---+\s*$|\|)/.test(line)) continue;
    const text = plain(line);
    if (text !== "") return text.slice(0, 200);
  }
  return "";
}

/** The normalised form links are matched by: trimmed, lower case, inner spaces collapsed. */
/**
 * "Work / Projects" for a note's notebook, outermost first (the note list, spec04 FR14).
 * Empty for no or an unknown notebook; stops on a cycle.
 */
export function notebookPath(
  notebookId: string | null,
  notebooks: ReadonlyArray<{ id: string; parentId: string | null; name: string }>,
): string {
  const byId = new Map(notebooks.map((n) => [n.id, n]));
  const names: string[] = [];
  const seen = new Set<string>();
  for (let id = notebookId; id && !seen.has(id); ) {
    seen.add(id);
    const notebook = byId.get(id);
    if (!notebook) break;
    names.unshift(notebook.name);
    id = notebook.parentId;
  }
  return names.join(" / ");
}

export const linkKey = (title: string) => title.trim().replace(/\s+/g, " ").toLowerCase();

export interface WikiLink {
  /** The linked title as written (before `|` alias). */
  target: string;
  /** The line it appears on, for "Linked from" (max 300 chars). */
  line: string;
}

/**
 * `[[Title]]` and `[[Title|shown text]]` links outside code (FR13). Each target once, in
 * order of first appearance.
 */
export function extractWikiLinks(content: string): WikiLink[] {
  const found = new Map<string, WikiLink>();
  let fence: string | null = null;
  for (const line of lines(content)) {
    const marker = line.match(/^\s*(```|~~~)/)?.[1];
    if (marker) {
      fence = fence === marker ? null : (fence ?? marker);
      continue;
    }
    if (fence) continue;
    const withoutCode = line.replace(/`[^`]*`/g, "");
    for (const match of withoutCode.matchAll(/\[\[([^[\]\n|]{1,200})(?:\|[^[\]\n]{0,200})?\]\]/g)) {
      const target = (match[1] ?? "").trim();
      const key = linkKey(target);
      if (key !== "" && !found.has(key)) {
        found.set(key, { target, line: plain(line).slice(0, 300) });
      }
    }
  }
  return [...found.values()];
}
